import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm, type FieldErrors } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Plus, Save, X } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { StickyActionBar } from "@/components/layout/StickyActionBar";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import {
  getLayoutNotificacaoDto,
  getRoles,
  saveLayoutNotificacaoDto,
  type Role,
} from "./api";
import { layoutNotificacaoSchema, type LayoutNotificacaoFormValues } from "./schema";

// Porte de app/controllers/form-layout-notificacao.controller.js +
// app/templates/notificacao/form-layout-notificacao.html. Sem TinyMCE — o
// template legado usa <textarea> plano em todo campo "corpo" (o plano
// original supôs rich-text aqui por engano; confirmado com o usuário que
// TinyMCE só entra onde já existia no legado, ou seja, não nesta tela).
const DEFAULT_VALUES: LayoutNotificacaoFormValues = {
  gatilho: "",
  descricao: "",
  seInterno: "S",
  seExibir: "S",
  sePortal: "S",
  seEmail: "S",
  sePush: "S",
};

export default function FormLayoutNotificacao() {
  const { idLayout } = useParams<{ idLayout: string }>();
  const isEdit = !!idLayout;
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);
  const [permissoesOpen, setPermissoesOpen] = useState(false);
  const [busca, setBusca] = useState("");
  // `null` = ainda não houve interação do usuário — usa a seleção vinda do
  // servidor. Evita sincronizar estado a partir de uma query num efeito
  // (react-hooks/set-state-in-effect); o fallback abaixo já resolve o valor
  // inicial sem precisar de setState disparado por useEffect.
  const [selecionadasOverride, setSelecionadasOverride] = useState<Set<string> | null>(null);

  const form = useForm<LayoutNotificacaoFormValues>({
    resolver: zodResolver(layoutNotificacaoSchema),
    defaultValues: DEFAULT_VALUES,
  });

  const { data: roles } = useQuery({ queryKey: ["role"], queryFn: getRoles });

  const { data: dto } = useQuery({
    queryKey: ["layout-notificacao", "dto", idLayout],
    queryFn: () => getLayoutNotificacaoDto(Number(idLayout)),
    enabled: isEdit,
  });

  useEffect(() => {
    if (dto) {
      form.reset(dto.layoutNotificacao);
    }
  }, [dto, form]);

  const selecionadas = selecionadasOverride ?? new Set(dto?.roles.map((r) => r.nome) ?? []);

  function setSelecionadas(updater: Set<string> | ((prev: Set<string>) => Set<string>)) {
    setSelecionadasOverride((prev) => {
      const base = prev ?? new Set(dto?.roles.map((r) => r.nome) ?? []);
      return typeof updater === "function" ? updater(base) : updater;
    });
  }

  const salvar = useMutation({
    mutationFn: (values: LayoutNotificacaoFormValues) => {
      const rolesSelecionadas = (roles ?? []).filter((r) => selecionadas.has(r.nome));
      // O zod descarta chaves fora do schema (situacao, categoria, ordem...);
      // o legado devolvia a entidade inteira, então sobrepomos os valores
      // validados ao registro original (ver D14 em docs/migracao/decisoes.md).
      return saveLayoutNotificacaoDto({
        layoutNotificacao: { ...dto?.layoutNotificacao, ...values },
        roles: rolesSelecionadas,
      });
    },
    onSuccess: () => {
      toast.success("Layout de notificação salvo.");
      qc.invalidateQueries({ queryKey: ["layout-notificacao"] });
      navigate("/LayoutNotificacao/Consulta");
    },
  });

  const rolesFiltradas = useMemo(() => {
    if (!busca) return roles ?? [];
    return (roles ?? []).filter((r) => r.descricao?.toLowerCase().includes(busca.toLowerCase()));
  }, [roles, busca]);

  function toggleRole(nome: string, checked: boolean) {
    setSelecionadas((prev) => {
      const next = new Set(prev);
      if (checked) next.add(nome);
      else next.delete(nome);
      return next;
    });
  }

  // Porte de marcarDesmarcarTodos(): sempre atua sobre TODAS as roles, não só
  // as filtradas — por isso o checkbox fica desabilitado enquanto há busca
  // ativa (mesmo comportamento do legado, ng-disabled="nomePermissao").
  function marcarTodos(checked: boolean) {
    setSelecionadas(checked ? new Set((roles ?? []).map((r) => r.nome)) : new Set());
  }

  const selecionadasList: Role[] = (roles ?? []).filter((r) => selecionadas.has(r.nome));

  function aoInvalidar(errors: FieldErrors<LayoutNotificacaoFormValues>) {
    if (Object.keys(errors).length > 0) {
      toast.error("Existem campos inválidos. Verifique os campos destacados em vermelho.");
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit((values) => salvar.mutate(values), aoInvalidar)} className="space-y-6 pb-20">
        <h1 className="text-lg font-semibold text-foreground">Cadastro de Layout de Notificação</h1>

        <div className="grid gap-4 sm:grid-cols-2">
          <FormField
            control={form.control}
            name="gatilho"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Gatilho</FormLabel>
                <FormControl>
                  <Input autoFocus {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="descricao"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Descrição</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="seInterno"
          render={({ field }) => (
            <FormItem className="flex items-center gap-2 space-y-0">
              <FormControl>
                <Checkbox checked={field.value === "S"} onCheckedChange={(c) => field.onChange(c ? "S" : "N")} />
              </FormControl>
              <FormLabel className="!mt-0 font-normal">
                Notificação interna{" "}
                <span className="italic text-muted-foreground">
                  (não aparece na listagem de preferências do cliente, enviada só para o painel admin)
                </span>
              </FormLabel>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="seExibir"
          render={({ field }) => (
            <FormItem className="flex items-center gap-2 space-y-0">
              <FormControl>
                <Checkbox checked={field.value === "S"} onCheckedChange={(c) => field.onChange(c ? "S" : "N")} />
              </FormControl>
              <FormLabel className="!mt-0 font-normal">
                Exibir nas preferências{" "}
                <span className="italic text-muted-foreground">
                  (para notificações obrigatórias que o usuário não pode deixar de receber)
                </span>
              </FormLabel>
            </FormItem>
          )}
        />

        <div className="flex flex-wrap gap-6">
          {(["sePortal", "seEmail", "sePush"] as const).map((name) => (
            <FormField
              key={name}
              control={form.control}
              name={name}
              render={({ field }) => (
                <FormItem className="flex items-center gap-2 space-y-0">
                  <FormControl>
                    <Checkbox checked={field.value === "S"} onCheckedChange={(c) => field.onChange(c ? "S" : "N")} />
                  </FormControl>
                  <FormLabel className="!mt-0 font-normal">
                    {name === "sePortal" ? "Portal" : name === "seEmail" ? "E-mail" : "Push (App)"}
                  </FormLabel>
                </FormItem>
              )}
            />
          ))}
        </div>

        <div className="space-y-2">
          <Button type="button" variant="outline" onClick={() => setPermissoesOpen(true)}>
            <Plus className="mr-2 h-4 w-4" /> Adicionar permissões
          </Button>
          <div className="flex flex-wrap gap-2">
            {selecionadasList.map((r) => (
              <Badge key={r.nome} variant="secondary" className="gap-1">
                {r.nome}
                <button type="button" onClick={() => toggleRole(r.nome, false)} title="remover">
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
        </div>

        <h2 className="text-base font-semibold text-foreground">Portal</h2>
        <FormField
          control={form.control}
          name="tituloPortal"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Título portal</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="corpoPortal"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Corpo portal</FormLabel>
              <FormControl>
                <Textarea rows={2} {...field} />
              </FormControl>
            </FormItem>
          )}
        />

        <h2 className="text-base font-semibold text-foreground">Navegador (Push web)</h2>
        <FormField
          control={form.control}
          name="tituloNavegador"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Título navegador</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="corpoNavegador"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Corpo navegador</FormLabel>
              <FormControl>
                <Textarea rows={2} {...field} />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="urlWeb"
          render={({ field }) => (
            <FormItem>
              <FormLabel>URL deeplink - Navegador</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
            </FormItem>
          )}
        />

        <h2 className="text-base font-semibold text-foreground">Push (App)</h2>
        <FormField
          control={form.control}
          name="tituloPush"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Título push</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="corpoPush"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Corpo push</FormLabel>
              <FormControl>
                <Textarea rows={2} {...field} />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="urlApp"
          render={({ field }) => (
            <FormItem>
              <FormLabel>URL deeplink - App</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
            </FormItem>
          )}
        />

        <h2 className="text-base font-semibold text-foreground">E-mail</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <FormField
            control={form.control}
            name="assuntoEmail"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Assunto e-mail</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="emailRemetente"
            render={({ field }) => (
              <FormItem>
                <FormLabel>E-mail remetente</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
              </FormItem>
            )}
          />
        </div>
        <FormField
          control={form.control}
          name="corpoEmail"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Corpo e-mail</FormLabel>
              <FormControl>
                <Textarea rows={4} {...field} />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="idTemplate"
          render={({ field }) => (
            <FormItem>
              <FormLabel>ID template - SendGrid</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
            </FormItem>
          )}
        />

        <h2 className="text-base font-semibold text-foreground">Documentação interna</h2>
        <FormField
          control={form.control}
          name="documentacao"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Textarea rows={6} {...field} />
              </FormControl>
            </FormItem>
          )}
        />

        <StickyActionBar>
          <Button type="submit" disabled={salvar.isPending}>
            {salvar.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            {salvar.isPending ? "Salvando..." : "Salvar"}
          </Button>
          <Button type="button" variant="outline" onClick={() => setConfirmCancelOpen(true)}>
            <X className="mr-2 h-4 w-4" /> Cancelar
          </Button>
        </StickyActionBar>
      </form>

      <ConfirmDialog
        open={confirmCancelOpen}
        onOpenChange={setConfirmCancelOpen}
        title="Cancelar alterações?"
        description="As alterações não salvas serão perdidas."
        confirmLabel="Sair sem salvar"
        onConfirm={() => navigate("/LayoutNotificacao/Consulta")}
      />

      <Dialog open={permissoesOpen} onOpenChange={setPermissoesOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Permissões</DialogTitle>
          </DialogHeader>
          <Input placeholder="Pesquisar..." value={busca} onChange={(e) => setBusca(e.target.value)} />
          <div className="max-h-[60vh] overflow-y-auto">
            {rolesFiltradas.length === 0 ? (
              <p className="py-4 text-center text-muted-foreground">Nenhuma permissão encontrada</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10">
                      <Checkbox
                        disabled={!!busca}
                        title={busca ? "Limpe o filtro para utilizar esta opção" : undefined}
                        checked={(roles ?? []).length > 0 && selecionadas.size === (roles ?? []).length}
                        onCheckedChange={(c) => marcarTodos(!!c)}
                      />
                    </TableHead>
                    <TableHead>Nome</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rolesFiltradas.map((r) => (
                    <TableRow key={r.nome}>
                      <TableCell>
                        <Checkbox
                          checked={selecionadas.has(r.nome)}
                          onCheckedChange={(c) => toggleRole(r.nome, !!c)}
                        />
                      </TableCell>
                      <TableCell>{r.nome}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
          <DialogFooter>
            <Button onClick={() => setPermissoesOpen(false)}>Confirmar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Form>
  );
}
